import { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../../config/apiConfig";
import "./OtpPage.css";

export function OtpPageView() {
  const navigate = useNavigate();
  const [otp, setOTP] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const email = localStorage.getItem("email");

  const validateForm = () => {
    const value = otp.trim();

    if (!value) {
      setError("OTP is required.");
      return false;
    }

    if (!/^\d{6}$/.test(value)) {
      setError("OTP must be a valid 6-digit number.");
      return false;
    }

    setError("");
    return true;
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const storedEmail = localStorage.getItem("email");

    if (!storedEmail) {
      setError("No email found. Please try again.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: storedEmail,
          otp: otp.trim(),
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        setError(data.message || "OTP verification failed. Please try again.");
        return;
      }

      navigate("/create-profile", {
        state: { email: storedEmail },
      });
    } catch {
      setError(
        "Unable to verify the OTP right now. Please check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setOTP(value);

    if (error) {
      setError("");
    }
  };

  return (
    <Box className="farmology-auth-page farmology-otp-page">
      <Box className="farmology-auth-container farmology-otp-container">
        <Box className="farmology-auth-card farmology-otp-card">
          {/* =================================================
              FORM PANEL
          ================================================= */}
          <Box className="farmology-auth-form-panel farmology-otp-form-panel">
            <div className="farmology-auth-brand">
              <div className="farmology-auth-logo-wrap">
                <img
                  src="/LOGOS/Farmology-Logo-e1687720251448.png"
                  alt="Farmology"
                  className="farmology-auth-logo"
                />
              </div>

              <Typography className="farmology-auth-eyebrow">
                ACCOUNT VERIFICATION
              </Typography>

              <Typography component="h1" className="farmology-auth-title">
                Verify your email.
              </Typography>

              <Typography className="farmology-auth-description">
                Enter the 6-digit verification code we sent to your email
                address to continue setting up your Farmology account.
              </Typography>
            </div>

            <Box
              component="form"
              onSubmit={handleVerifyOTP}
              className="farmology-auth-form"
              noValidate
            >
              <div className="farmology-otp-email">
                <span>CODE SENT TO</span>
                <strong>{email || "Email not available"}</strong>
              </div>

              <TextField
                fullWidth
                name="otp"
                label="Verification code"
                value={otp}
                onChange={handleOtpChange}
                error={Boolean(error)}
                helperText={error}
                className="farmology-auth-field farmology-otp-field"
                variant="outlined"
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
                disabled={loading}
              />

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                disabled={loading}
                className="farmology-auth-submit"
              >
                Verify
                <span className="farmology-auth-arrow">→</span>
              </LoadingButton>

              <Typography className="farmology-auth-signin">
                Enter the code exactly as received in your inbox.
              </Typography>

              <Typography className="farmology-auth-footer-note">
                Farmology uses verification codes to keep your account secure.
              </Typography>
            </Box>
          </Box>

          {/* =================================================
              VISUAL PANEL
          ================================================= */}
          <Box className="farmology-auth-visual-panel farmology-otp-visual-panel">
            <div className="farmology-auth-visual-copy">
              <Typography className="farmology-auth-visual-eyebrow">
                FARMOLOGY PLATFORM
              </Typography>

              <Typography
                component="h2"
                className="farmology-auth-visual-title"
              >
                One step closer to smarter farming.
              </Typography>

              <Typography className="farmology-auth-visual-text">
                Verify your email and continue to access Farmology tools
                designed to bring better intelligence to every field.
              </Typography>
            </div>

            <div className="farmology-otp-code-visual" aria-hidden="true">
              <div className="farmology-otp-code-card">
                <span>VERIFICATION CODE</span>
                <div className="farmology-otp-digits">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>

            <div className="farmology-auth-stat-card">
              <span className="farmology-auth-stat-label">SECURE ACCESS</span>
              <strong>Email verification required</strong>
            </div>

            <div className="farmology-auth-circle" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default OtpPageView;
